import { getAuthClient } from '../../auth';
import { FLOOR_PLAN_API_INCLUDES } from '../FloorPlan/FloorPlanHelpers';

const auth = getAuthClient();

/**
 * Assemble residence API includes for JSON:API requests.
 * Used by PibPage for favorite residences.
 */
export const assembleResidenceAPIIncludes = () => {
  const baseIncludes = FLOOR_PLAN_API_INCLUDES;
  // Remove field_downloadable_pdf from base includes.
  const filteredBaseIncludes = baseIncludes.filter( ( item ) => item !== 'field_downloadable_pdf' );

  const floorPlanIncludes = FLOOR_PLAN_API_INCLUDES.map( ( include ) => `field_floor_plan.${include}` );
  const residenceIncludes = [
    'field_building_grouping',
    'field_floor_plan',
    'field_gallery_photos',
    'field_level',
    'field_residents',
    'field_status',
    'field_view_gallery',
    'field_view_gallery.field_gallery_photos',
    'field_view_photos',
    'field_view_video',
  ];
  const includes = [
    ...filteredBaseIncludes,
    ...floorPlanIncludes,
    ...residenceIncludes,
  ];
  return includes;
};

export function assembleResidenceNavBtnsData( unitData ) {
  const navBtns = [];

  if ( !unitData ) {
    return navBtns;
  }

  const {
    twoDFloorPlan,
    threeDFloorPlan,
    roomDims,
    virtualTour,
    finishCollection,
    furnitureConfiguratorUrl,
    video,
    view,
    photoGallery,
  } = unitData;

  navBtns.push( {
    section: 'info-scroll',
    text: 'Info',
  } );

  if ( twoDFloorPlan || threeDFloorPlan ) {
    navBtns.push( {
      section: 'floorplan-scroll',
      text: 'Floor Plan',
    } );
  }

  if ( roomDims?.length > 0 ) {
    navBtns.push( {
      section: 'dimensions-scroll',
      text: 'Dimensions',
    } );
  }

  if ( view.length > 0 ) {
    navBtns.push( {
      section: 'view-scroll',
      text: 'View',
    } );
  }

  if ( photoGallery?.images.length > 0 ) {
    navBtns.push( {
      section: 'slideshow-scroll',
      text: 'Photos',
    } );
  }

  if ( video?.videoId && video?.service ) {
    navBtns.push( {
      section: 'video-scroll',
      text: 'Video',
    } );
  }

  if ( virtualTour?.threeSixties.length > 0 ) {
    navBtns.push( {
      section: 'tour-scroll',
      text: 'Tour',
    } );
  }

  if ( furnitureConfiguratorUrl ) {
    navBtns.push( {
      section: 'configurator-scroll',
      text: 'Furniture Layout',
    } );
  }

  if ( finishCollection && Object.keys( finishCollection ).length !== 0 ) {
    navBtns.push( {
      section: 'finishes-scroll',
      text: 'Finishes',
    } );
  }

  return navBtns;
}

export function assembleLocationString( buildingGroupingName, levelName ) {
  const locationArray = [];
  if ( buildingGroupingName && buildingGroupingName !== '' ) {
    locationArray.push( buildingGroupingName );
  }

  if ( levelName && levelName !== '' ) {
    locationArray.push( levelName );
  }

  const location = locationArray.length > 0 ? locationArray.join( ', ' ) : null;
  return location;
}

/**
 * Fetch normalized residence data from the custom API endpoint.
 * This endpoint returns pre-normalized data, so no client-side transformation needed.
 *
 * @param {string} uuid - The residence UUID.
 * @param {string} [mode] - Optional mode (e.g., 'preview').
 * @returns {Object} The normalized residence data.
 */
export const getNormalizedResidenceData = async ( uuid, mode ) => {
  const modeParam = mode ? `&mode=${mode}` : '';
  const response = await auth.fetchWithAuthentication(
    `/your_tour_api/residence/${uuid}?_format=json${modeParam}`,
    'GET',
  );

  if ( !response || response.error ) {
    // eslint-disable-next-line no-console
    console.error( 'Error fetching residence data:', response?.error || 'Unknown error' );
    return null;
  }

  // The endpoint returns data in the format React expects.
  // Map some fields for compatibility with existing components.
  return {
    ...response,
    // Ensure roomData is mapped to roomDims for assembleResidenceNavBtnsData
    roomDims: response.roomData,
    // Map floor plan images for nav button checks
    twoDFloorPlan: response.twoD,
    threeDFloorPlan: response.threeD,
    // Map virtual tour for nav button checks
    virtualTour: response.threeSixties?.length > 0 ? { threeSixties: response.threeSixties } : null,
    // Map photo gallery for nav button checks
    photoGallery: response.photoGalleryImages?.length > 0 ? { images: response.photoGalleryImages } : null,
  };
};

/**
 * Patch residence in Drupal.
 *
 * @param {String} id
 *    Residence UUID.
 * @param {Array} residentUuids
 *    Resident UUIDs.
 * @param {String} statusUuid
 *    Status UUID.
 * @returns {Object}
 */
export async function patchResidence(
  id,
  residentUuids,
  statusUuid,
) {
  if ( !id && (
    ( !residentUuids || residentUuids.length === 0 ) && !statusUuid )
  ) {
    // eslint-disable-next-line no-console
    console.error( 'Cannot patch residence. Missing id, resident uuid(s), or status uuid' );
    return {};
  }

  const data = {
    data: {
      type: 'node--residence',
      id,
    },
  };

  if ( statusUuid || residentUuids ) {
    data.data.relationships = {};
  }

  if ( residentUuids ) {
    const residentRelationshipData = [];
    residentUuids.forEach( ( uuid ) => {
      const residentRelationshipDatum = {
        type: 'node--resident',
        id: uuid,
      };
      residentRelationshipData.push( residentRelationshipDatum );
    } );

    data.data.relationships.field_residents = {
      data: residentRelationshipData,
    };
  }

  if ( statusUuid ) {
    data.data.relationships.field_status = {
      data: {
        type: 'taxonomy_term--residence_status',
        id: statusUuid,
      },
    };
  }

  const body = JSON.stringify( data );
  const updatedResidence = await auth.fetchWithAuthentication(
    `/jsonapi/node/residence/${id}`,
    'PATCH',
    body,
  );

  return updatedResidence;
}
